'use server';

import { apiRequest } from '@/utils/api-request';

const ADMIN = '/api/parent-app/admin';

export interface FeeSummary {
  total_billed: number;
  total_paid: number;
  total_pending: number;
  overdue_count: number;
}

export interface FeePayment {
  id: number;
  amount: number;
  payment_date: string;
  payment_note: string | null;
}

export interface Installment {
  id: number;
  installment_name: string;
  amount: number;
  due_date: string;
  paid_amount: number;
  balance: number;
  status: 'paid' | 'partial' | 'pending' | 'overdue';
  academic_year: string | null;
  academic_year_id: number;
  payments: FeePayment[];
}

export interface StudentFeeRow {
  student_id: number;
  student_name: string;
  class_name: string;
  total_billed: number;
  total_paid: number;
  balance: number;
  status: 'paid' | 'partial' | 'pending' | 'overdue';
  installments: Installment[];
}

export const getFeeSummary = (academic_year_id?: number, class_ids?: string[]) => {
  const qs = new URLSearchParams();
  if (academic_year_id) qs.set('academic_year_id', String(academic_year_id));
  (class_ids || []).forEach(id => qs.append('class_id[]', id));
  const q = qs.toString();
  return apiRequest<FeeSummary>(`${ADMIN}/fees/summary${q ? `?${q}` : ''}`, { method: 'GET' });
};

export interface StudentFeesResponse {
  data: StudentFeeRow[];
  total: number;
  page: number;
  limit: number;
}

export const getStudentFees = (params?: {
  academic_year_id?: number;
  class_ids?: string[];
  status?: string;
  search?: string;
  page?: number;
  limit?: number;
}) => {
  const qs = new URLSearchParams();
  if (params?.academic_year_id) qs.set('academic_year_id', String(params.academic_year_id));
  (params?.class_ids || []).forEach(id => qs.append('class_id[]', id));
  if (params?.status && params.status !== 'all') qs.set('status', params.status);
  if (params?.search)           qs.set('search', params.search);
  if (params?.page)             qs.set('page', String(params.page));
  if (params?.limit)            qs.set('limit', String(params.limit));
  const q = qs.toString();
  return apiRequest<StudentFeesResponse>(`${ADMIN}/fees${q ? `?${q}` : ''}`, { method: 'GET' });
};

export const recordFeePayment = (body: {
  student_id: number;
  fee_structure_id?: number | null;
  amount: number;
  payment_date: string;
  payment_method: string;
  note?: string;
}) => apiRequest(`${ADMIN}/fees/record-payment`, { method: 'POST', body: JSON.stringify(body) });

export const createInstallment = (body: {
  student_id: number;
  installment_name: string;
  installment_amount: number;
  due_date: string;
  academic_year_id?: number | null;
}) => apiRequest(`${ADMIN}/fees/installments`, { method: 'POST', body: JSON.stringify(body) });

export const updateInstallment = (id: number, body: {
  installment_name?: string;
  installment_amount?: number;
  due_date?: string;
}) => apiRequest(`${ADMIN}/fees/installments/${id}`, { method: 'PUT', body: JSON.stringify(body) });

export const deleteInstallment = (id: number) =>
  apiRequest(`${ADMIN}/fees/installments/${id}`, { method: 'DELETE' });

// ─── Notification types ───────────────────────────────────────────────────────

export interface NotificationSettings {
  id: number;
  school_id: number;
  whatsapp_enabled: boolean;
  due_reminder_enabled: boolean;
  due_reminder_days: number;
  due_today_enabled: boolean;
  overdue_enabled: boolean;
  overdue_frequency_days: number;
  payment_confirmation_enabled: boolean;
  partial_payment_enabled: boolean;
}

export interface NotificationHistoryItem {
  id: number;
  student_id: number;
  student_name: string;
  notification_type: string;
  phone: string;
  status: 'sent' | 'failed' | 'pending';
  sent_at: string | null;
  created_at: string;
  remarks: string | null;
  installment_id: number | null;
}

export const getNotificationSettings = () =>
  apiRequest<NotificationSettings>(`${ADMIN}/fees/notifications/settings`, { method: 'GET' });

export const updateNotificationSettings = (body: Partial<Omit<NotificationSettings, 'id' | 'school_id'>>) =>
  apiRequest<NotificationSettings>(`${ADMIN}/fees/notifications/settings`, { method: 'PUT', body: JSON.stringify(body) });

export const previewBulkSend = (params: { type: string; academic_year_id?: number; class_ids?: string[] }) => {
  const qs = new URLSearchParams();
  qs.set('type', params.type);
  if (params.academic_year_id) qs.set('academic_year_id', String(params.academic_year_id));
  (params.class_ids || []).forEach(id => qs.append('class_id[]', id));
  return apiRequest<{ eligible: number; no_phone: number; already_sent: number }>(
    `${ADMIN}/fees/notifications/preview?${qs.toString()}`, { method: 'GET' }
  );
};

export const executeBulkSend = (body: { type: string; academic_year_id?: number; class_ids?: string[] }) =>
  apiRequest<{ sent: number; failed: number; skipped_no_phone: number; skipped_dedup: number }>(
    `${ADMIN}/fees/notifications/send`, { method: 'POST', body: JSON.stringify(body) }
  );

export const getNotificationHistory = (params?: { student_id?: number; type?: string; status?: string; page?: number; limit?: number }) => {
  const qs = new URLSearchParams();
  if (params?.student_id) qs.set('student_id', String(params.student_id));
  if (params?.type)       qs.set('type', params.type);
  if (params?.status)     qs.set('status', params.status);
  if (params?.page)       qs.set('page', String(params.page));
  if (params?.limit)      qs.set('limit', String(params.limit));
  const q = qs.toString();
  return apiRequest<{ data: NotificationHistoryItem[]; total: number; page: number; limit: number }>(
    `${ADMIN}/fees/notifications/history${q ? `?${q}` : ''}`, { method: 'GET' }
  );
};
